import { el, clear } from './dom.js';
import { icon } from './icons.js';
import { toast } from './toast.js';
import { toggleTheme, effectiveTheme } from './theme.js';
import { extractCode, buildMeetingURL, externalLink } from '../lib/codes.js';
import { mintRoomCode } from '../lib/rooms.js';
import { copyText } from './panels.js';
import { formatClock, formatDay } from '../lib/time.js';
import { accountAvatar, showAccountAvatar } from './account-avatar.js';
import { isSignedIn, getUser, signOut, requestSignIn, onAuthChange, authErrorMessage } from '../lib/auth.js';
import { clearAgendaCaches } from '../lib/agenda.js';
import { clearMeetingState } from '../lib/prefs.js';
import { brandLockup } from './brand.js';

/**
 * Home screen: start or join a meeting.
 * @param {HTMLElement} root
 * @param {object} ctx  app context (openPrejoin, getUserName, ...)
 * @returns {() => void} cleanup
 */
export function mountHome(root, ctx) {
  clear(root);

  let mounted = true; // false once the view unmounts (async work may outlive it)

  // The signed-in host, read from the token once per sign-in state (display only).
  let userPromise = null;
  const currentUser = () => (userPromise ??= fetchUser());
  const forgetUser = () => {
    userPromise = null;
  };

  const clock = el('span', { class: 'home-clock' }, clockText());
  const themeBtn = el(
    'button',
    { class: 'icon-btn', 'aria-label': 'Toggle theme', title: 'Toggle light/dark theme', onclick: onThemeToggle },
    icon(effectiveTheme() === 'dark' ? 'sun' : 'moon')
  );

  const userSlot = el('div', { class: 'home-user' });
  let closeSettings = null;
  const settingsBtn = el('button', {
    class: 'icon-btn', 'aria-label': 'Settings', title: 'Settings',
    onclick: async () => {
      closeSettings?.();
      closeSettings = null;
      // The settings dialog is loaded when first opened, not with the page.
      const { openHomeSettings } = await import('./nickname.js');
      if (mounted) closeSettings = openHomeSettings(root, ctx, settingsBtn);
    },
  }, icon('settings'));

  const joinInput = el('input', {
    type: 'text',
    placeholder: 'Enter a code or link',
    'aria-label': 'Meeting code or link',
    autocomplete: 'off',
    autocorrect: 'off',
    autocapitalize: 'off',
    spellcheck: 'false',
    onkeydown: (e) => {
      if (e.key === 'Enter') onJoin();
    },
    oninput: () => {
      joinBtn.disabled = !joinInput.value.trim();
    },
  });
  const joinBtn = el('button', { class: 'btn home-join-btn', disabled: true, onclick: onJoin }, 'Join');
  const signedIn = isSignedIn();
  const authNotice = signedIn ? null : ctx.takeAuthNotice?.();
  // The only way to sign in: trtCRM does the authenticating and sends the browser back with a token.
  const signInBtn = el(
    'button',
    {
      class: 'btn btn-primary home-signin',
      onclick: () => {
        if (requestSignIn() === 'unavailable') toast('trtCRM sign-in is not configured for this site.', { icon: 'info' });
      },
    },
    'Sign in with trtCRM'
  );
  const newMeetingLabel = el('span', {}, 'New meeting');
  const newMeetingBtn = el(
    'button',
    { class: 'btn btn-primary', 'aria-haspopup': 'menu', 'aria-expanded': 'false', onclick: toggleNewMenu },
    icon('videoCall'),
    newMeetingLabel
  );

  const view = el(
    'div',
    { class: 'home' },
    el(
      'header',
      { class: 'home-header' },
      brandLockup(),
      el('div', { class: 'home-header-actions' }, clock, settingsBtn, themeBtn, userSlot)
    ),
    el(
      'main',
      { class: 'home-main' },
      el(
        'section',
        { class: 'home-hero' },
        signedIn
          ? el('h1', {}, 'Video meetings, ', el('span', { class: 'home-hero-accent' }, 'right in your browser.'))
          : el('h1', {}, 'Sign in to ', el('span', { class: 'home-hero-accent' }, 'host a meeting.')),
        authNotice ? el('p', { class: 'home-auth-notice', role: 'alert' }, authErrorMessage(authNotice)) : null,
        el(
          'p',
          { class: 'home-lede' },
          signedIn
            ? 'Start a call in seconds, or create a link to share for later. It all runs in your browser.'
            : 'Hosts sign in with trtCRM. Joining a meeting needs no account: paste the link or code you were given.'
        ),
        el(
          'div',
          { class: 'home-actions' },
          el('div', { class: 'home-new' }, signedIn ? newMeetingBtn : signInBtn),
          el('div', { class: 'home-join' }, el('div', { class: 'text-field' }, icon('keyboard'), joinInput, joinBtn))
        ),
        el(
          'ul',
          { class: 'home-features' },
          feature('Nothing to install'),
          feature('Links never expire'),
          feature('No account needed to join')
        )
      ),
      // Signed in through the CRM, the day's meetings sit beside the hero,
      // each one click from joining. Otherwise (see lib/crm.js) the hero
      // stands alone — with a line saying where the list lives.
      ctx.calendar?.supported()
        ? el('aside', { class: 'home-agenda' })
        : !signedIn
          ? el('aside', { class: 'home-agenda home-booking' })
          : null
    )
  );

  root.append(view);
  renderUser();
  // A token can arrive while the page is open (posted by the embedding CRM).
  // Signing in or out (a token can arrive from the embedding CRM) changes the whole screen.
  const stopAuthWatch = onAuthChange(() => ctx.goHome());

  let unmountAgenda = null;
  let agendaRun = 0; // bumped to drop a panel that is still loading when the account changes or the view goes
  const agendaHost = view.querySelector('.home-agenda:not(.home-booking)');
  mountAgendaPanel();
  // Members see the hours visitors booked, under the hero on the left; their meetings stay in the right column.
  let unmountAppointments = null;
  const heroHost = view.querySelector('.home-hero');
  if (heroHost && signedIn) void import('./appointments.js').then(({ mountAppointments }) => { if (mounted) unmountAppointments = mountAppointments(heroHost, { ctx }); });
  // Visitors who are not signed in can book an hour with the team.
  const bookingHost = view.querySelector('.home-booking');
  let unmountBooking = null;
  if (bookingHost) void import('./booking.js').then(({ mountBooking }) => { if (mounted) unmountBooking = mountBooking(bookingHost); });

  /**
   * The meetings panel is scoped to the signed-in account — its snapshot is
   * keyed by it, and what it shows is theirs. So it is mounted afresh when
   * the account changes: the one that was there would otherwise keep
   * showing the last account's meetings, and write its next answer under
   * that account's key.
   */
  function mountAgendaPanel() {
    unmountAgenda?.();
    unmountAgenda = null;
    if (!agendaHost) return;
    const run = ++agendaRun;
    // The panel is loaded when there is a CRM to list, not with the page.
    void import('./agenda.js').then(({ mountAgenda }) => {
      if (!mounted || run !== agendaRun) return;
      unmountAgenda = mountAgenda(agendaHost, {
        calendar: ctx.calendar,
        ctx,
        // Scopes the panel's local snapshot to this account, so a shared
        // device never paints one person's meetings for the next.
        userKey: currentUser().then((user) => user?.sub ?? ''),
        // The CRM's own calendar, in a new tab (the CRM's origin comes from the deployment's config).
        openCalendar: () => {
          const crm = window.TRTMEET_CONFIG?.crmOrigin;
          if (crm) window.open(new URL('/calendar', crm).href, '_blank', 'noopener,noreferrer');
          else toast('No CRM is configured.', { icon: 'info' });
        },
      });
    });
  }

  const clockTimer = setInterval(() => {
    clock.textContent = clockText();
  }, 10_000);

  function clockText() {
    const now = new Date();
    return `${formatClock(now)} • ${formatDay(now)}`;
  }

  function onThemeToggle() {
    const next = toggleTheme();
    clear(themeBtn).append(icon(next === 'dark' ? 'sun' : 'moon'));
  }

  let newMenu = null;
  let menuDismissTimer = null;

  function toggleNewMenu() {
    if (newMenu) {
      closeNewMenu();
      return;
    }
    newMenu = el(
      'div',
      { class: 'menu home-new-menu', role: 'menu' },
      menuItem('videoCall', 'Start an instant meeting', () => {
        closeNewMenu();
        newMeetingBtn.focus();
        void onStartInstant();
      }),
      menuItem('calendar', 'Choose from Calendar…', () => {
        closeNewMenu();
        newMeetingBtn.focus();
        void import('./calendar-picker.js').then((m) => m.openCalendarPicker({ calendar: ctx.calendar, ctx }));
      }),
      menuItem('link', 'Create a meeting for later', () => {
        closeNewMenu();
        // Removing the focused menu item drops focus to <body>; return it to
        // the trigger while creation runs (the dialog takes focus when ready).
        newMeetingBtn.focus();
        onCreateForLater();
      })
    );
    newMeetingBtn.parentElement.append(newMenu);
    newMeetingBtn.setAttribute('aria-expanded', 'true');
    newMenu.querySelector('.menu-item')?.focus();
    document.addEventListener('keydown', onMenuKeydown);
    // Dismiss on any click outside the menu. Registration is deferred past
    // the opening click; closeNewMenu cancels it if the menu closes first
    // (e.g. via Escape), so no stale listener survives to kill the next open.
    menuDismissTimer = setTimeout(() => document.addEventListener('click', onDocClick), 0);
  }

  function onDocClick(e) {
    if (newMenu && !newMenu.contains(e.target)) closeNewMenu();
  }

  function onMenuKeydown(e) {
    if (!newMenu) return;
    if (e.key === 'Escape') {
      closeNewMenu();
      newMeetingBtn.focus();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const items = [...newMenu.querySelectorAll('.menu-item')];
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const next = (items.indexOf(document.activeElement) + step + items.length) % items.length;
      items[next]?.focus();
    } else if (e.key === 'Tab') {
      // Hand focus back to the trigger so the default Tab lands on the
      // control after (or before) it, not on document.body.
      closeNewMenu();
      newMeetingBtn.focus();
    }
  }

  function closeNewMenu() {
    clearTimeout(menuDismissTimer);
    document.removeEventListener('click', onDocClick);
    document.removeEventListener('keydown', onMenuKeydown);
    newMeetingBtn.setAttribute('aria-expanded', 'false');
    newMenu?.remove();
    newMenu = null;
  }

  function menuItem(name, label, onclick) {
    return el('button', { class: 'menu-item', role: 'menuitem', onclick }, icon(name), label);
  }

  let starting = false; // an instant meeting is on its way

  /**
   * Straight into a meeting when the app already knows who this is
   * (src/main.js startInstantMeeting); otherwise the pre-join screen, which
   * replaces this view at once. Starting can take a moment — a permission
   * prompt, the room's entry being written — so the button says so, and
   * takes no second click meanwhile.
   */
  async function onStartInstant() {
    if (starting) return;
    starting = true;
    newMeetingBtn.disabled = true;
    newMeetingLabel.textContent = 'Starting…';
    try {
      await ctx.startInstantMeeting();
    } finally {
      starting = false;
      newMeetingBtn.disabled = false;
      newMeetingLabel.textContent = 'New meeting';
    }
  }

  let closeLinkDialog = null; // active meeting-link dialog's close(), if open

  /**
   * Mint a room code now. Nothing is created anywhere — a room exists from
   * the moment someone with the link starts a meeting on it (lib/rooms.js)
   * — so this needs no account and no network, and can never fail.
   */
  function onCreateForLater() {
    showMeetingLinkDialog(mintRoomCode());
  }

  function showMeetingLinkDialog(code) {
    const url = buildMeetingURL(code);
    const close = () => {
      window.removeEventListener('keydown', onDialogKeydown);
      closeLinkDialog = null;
      backdrop.remove();
      if (newMeetingBtn.isConnected) newMeetingBtn.focus();
    };
    const onDialogKeydown = (e) => {
      if (e.key === 'Escape') {
        close();
      } else if (e.key === 'Tab') {
        // aria-modal promises focus stays inside — enforce it by wrapping
        // at the edges (and pulling focus in from the container itself).
        const focusables = [...dialog.querySelectorAll('button')];
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const current = document.activeElement;
        const inside = dialog.contains(current) && current !== dialog;
        if (e.shiftKey && (!inside || current === first)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (!inside || current === last)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    const dialog = el(
      'div',
      { class: 'dialog', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Meeting link', tabindex: '-1' },
      el(
        'div',
        { class: 'dialog-header' },
        el('h2', {}, 'Here’s your meeting link'),
        el('button', { class: 'icon-btn', 'aria-label': 'Close', onclick: close }, icon('close'))
      ),
      el(
        'div',
        { class: 'dialog-body' },
        el(
          'p',
          { class: 'home-link-note' },
          'Send this link to people you want to meet with, or drop it in a calendar invite. It never expires. When it’s time, open it to start the meeting — you, or anyone else holding the link, can. Guests who arrive early wait until someone does.'
        ),
        el('div', { class: 'info-link-box' }, url),
        el(
          'button',
          {
            class: 'btn btn-primary',
            style: { alignSelf: 'flex-start' },
            onclick: () => copyText(url, 'Meeting link copied'),
          },
          icon('copy'),
          'Copy meeting link'
        )
      )
    );
    // Only a click that both starts and ends on the backdrop dismisses.
    // A drag that starts inside the dialog (e.g. selecting the link text)
    // and ends outside still fires a click targeting the backdrop.
    let pressedBackdrop = false;
    const backdrop = el(
      'div',
      {
        class: 'dialog-backdrop',
        onpointerdown: (e) => {
          pressedBackdrop = e.target === backdrop;
        },
        onclick: (e) => {
          if (pressedBackdrop && e.target === backdrop) close();
        },
      },
      dialog
    );
    closeLinkDialog = close;
    window.addEventListener('keydown', onDialogKeydown);
    root.append(backdrop);
    dialog.focus();
  }

  function onJoin() {
    // A Google Meet, Zoom or other link is not one of our rooms: it opens in its own tab.
    const outside = externalLink(joinInput.value);
    if (outside) {
      window.open(outside, '_blank', 'noopener,noreferrer');
      return;
    }
    const code = extractCode(joinInput.value);
    if (!code) {
      toast('Enter a meeting code or paste a meeting link.', { icon: 'info' });
      return;
    }
    ctx.openPrejoin({ mode: 'join', code });
  }

  /** The signed-in host, or null. */
  async function fetchUser() {
    return getUser();
  }

  async function renderUser() {
    clear(userSlot);
    if (isSignedIn()) {
      const avatar = accountAvatar();
      const user = await currentUser();
      if (!mounted) return;
      const name = user?.name ?? 'You';
      userSlot.append(
        avatar,
        el('span', { class: 'home-user-name' }, name),
        el(
          'button',
          {
            class: 'icon-btn',
            title: 'Sign out',
            'aria-label': 'Sign out',
            onclick: () => {
              // Clears the app's session, then sends the browser through the CRM's logout and back here.
              void signOut();
              forgetUser();
              clearMeetingState();
              // Whoever signs in next must not be shown this account's
              // meetings from the panel's snapshot — nor by the panel
              // itself, which is mounted afresh for the account now here.
              clearAgendaCaches();
              renderUser();
              mountAgendaPanel();
            },
          },
          icon('logout')
        )
      );
      showAccountAvatar(avatar, user);
      // The account name is only a fallback — never clobber a
      // display name the user chose earlier.
      if (!ctx.getUserName?.()) ctx.setUserName?.(name);
    } else {
      // Nobody signed in: the way in is always at the top right, next to where the account will be.
      userSlot.append(
        el(
          'button',
          {
            class: 'btn btn-tonal home-header-signin',
            type: 'button',
            onclick: () => {
              if (requestSignIn() === 'unavailable') toast('trtCRM sign-in is not configured for this site.', { icon: 'info' });
            },
          },
          icon('logout'),
          'Sign in'
        )
      );
    }
  }

  return () => {
    mounted = false;
    agendaRun++;
    stopAuthWatch();
    clearInterval(clockTimer);
    closeNewMenu();
    closeLinkDialog?.();
    closeSettings?.();
    unmountAgenda?.();
    unmountBooking?.();
    unmountAppointments?.();
  };
}

function feature(text) {
  return el('li', {}, icon('check'), text);
}

